import { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { formatPhoneForLinks } from '../../../utility/phoneHelper'

import ProfileAvatar from '../../common/avatar/ProfileAvatar'

import { ReactComponent as ChevronIcon } from '../../../assets/images/common/icons/arrowDown.svg'

import classes from './EnrollmentDetailDrawer.module.scss'

// The drawer's student header: avatar, name and a link to the member, plus inline approve/decline
// while the enrollment is pending. The family admin's contact details expand from here rather than
// living in their own tab, so the coach can read them without leaving the enrollment.
const EnrollmentStudentHeader = ({ student, displayStatus, actionLoading, onApprove, onDecline }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [contactOpen, setContactOpen] = useState(false)

  const familyAdmin = student.familyAdmins?.[0]
  const phone = familyAdmin?.phonePrefix && familyAdmin?.phone ? `${familyAdmin.phonePrefix} ${familyAdmin.phone}` : null

  return (
    <div className={classes.studentHeader}>
      <div className={classes.studentRow}>
        <ProfileAvatar student={student} avatarClassName='small-round-image-wrapper' />
        <div className={classes.studentInfo}>
          <h6 className='mb-0 font-weight-bold'>{student.firstName} {student.lastName}</h6>
          <button className={classes.viewMemberLink} onClick={() => navigate(`/profile/${student.id}`)}>
            {t('enrollmentsBilling.drawer.viewMember')}
          </button>
        </div>

        <div className={classes.headerActions}>
          {displayStatus === 'pending_approval' && (
            <>
              <button className={classes.approveButton} onClick={onApprove} disabled={actionLoading}>
                {t('enrollmentsBilling.drawer.approve')}
              </button>
              <button className={classes.declineButton} onClick={onDecline} disabled={actionLoading}>
                {t('enrollmentsBilling.drawer.decline')}
              </button>
            </>
          )}
          {/* Nothing to expand when the student has no family admin on record. */}
          {familyAdmin && (
            <button
              className={classes.contactToggle}
              onClick={() => setContactOpen(open => !open)}
              aria-expanded={contactOpen}
            >
              {contactOpen ? t('enrollmentsBilling.drawer.hideContactInfo') : t('enrollmentsBilling.drawer.contactInfo')}
              <ChevronIcon
                width={14}
                height={14}
                className={classNames(classes.contactChevron, { [classes.contactChevronUp]: contactOpen })}
              />
            </button>
          )}
        </div>
      </div>

      {contactOpen && familyAdmin && (
        <div className={classes.contactPanel}>
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.familyAdmin')}</span>
            <span className={classes.detailValue}>{familyAdmin.firstName} {familyAdmin.lastName}</span>
          </div>
          {familyAdmin.email && (
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.contactEmail')}</span>
              {/* Reads as the design's plain value, but stays a mailto: - the tab this replaced had
                  the email and phone as links, and coaches use them to reach the parent. */}
              <a className={classNames(classes.detailValue, classes.contactLink)} href={`mailto:${familyAdmin.email}`}>
                {familyAdmin.email}
              </a>
            </div>
          )}
          {phone && (
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.contactPhone')}</span>
              <a
                className={classNames(classes.detailValue, classes.contactLink)}
                href={`tel:${formatPhoneForLinks(familyAdmin.phonePrefix, familyAdmin.phone)}`}
              >
                {phone}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

EnrollmentStudentHeader.propTypes = {
  student: PropTypes.object.isRequired,
  displayStatus: PropTypes.string,
  actionLoading: PropTypes.bool,
  onApprove: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
}

export default EnrollmentStudentHeader
